import {
  calendarDate,
  parseCalendarDate,
  resolveEventDate,
  type DatePrecision,
  type Season,
  type SemanticDate,
  type EventDate,
  resolveMonthAlternatives,
} from './event-date'

export type DateClarificationSuggestion = {
  kind: 'year' | 'season_year' | 'date'
  sourceText: string
  season: Season | null
}
type AgeYearQuestion = {
  kind: 'age_year'
  sourceText: string
  season: null
  yearOptions: number[]
}
type YearRangeQuestion = {
  kind: 'year_range'
  sourceText: string
  season: null
  yearOptions: number[]
}
type MonthDayQuestion = {
  kind: 'month_day'
  sourceText: string
  season: null
  month: number
  day: number
  referenceDate?: string
}
type MonthChoicesQuestion = {
  kind: 'month_choices'
  sourceText: string
  season: null
  monthOptions: Array<{ month: number; year: number }>
}
export type DateClarification = (
  | DateClarificationSuggestion
  | AgeYearQuestion
  | YearRangeQuestion
  | MonthDayQuestion
  | MonthChoicesQuestion
) & {
  status: 'pending' | 'dismissed' | 'answered'
}

// Questions are templates, not arbitrary model-written requests for personal details.
export function normalizeDateClarification(
  value: unknown,
  narrative: string,
  precision: DatePrecision | null | undefined,
  referenceDate?: string,
): DateClarification | null {
  if (precision === 'range') {
    const months = monthAlternativesClarification(narrative)
    if (months) return months
  }
  if (precision === 'unknown') {
    const storedReference = value && typeof value === 'object' && (value as Partial<MonthDayQuestion>).kind === 'month_day'
      ? (value as Partial<MonthDayQuestion>).referenceDate : undefined
    const partialDate = missingYearClarification(narrative, referenceDate ?? storedReference)
    if (partialDate) return partialDate
    const schoolDate = schoolYearClarification(narrative)
    if (schoolDate) return schoolDate
    const decade = decadeClarification(narrative)
    if (decade) return decade
  }
  if (!value || typeof value !== 'object') return null
  const rangeQuestion = value as Partial<YearRangeQuestion>
  if (rangeQuestion.kind === 'year_range') {
    const source = rangeQuestion.sourceText
    const years = rangeQuestion.yearOptions
    if (
      precision !== 'range' ||
      typeof source !== 'string' ||
      source.length > 120 ||
      !narrative.toLowerCase().includes(source.toLowerCase()) ||
      !/\b(?:when I was \d{1,3} or \d{1,3}|\d{4}\s+or\s+\d{4})\b/i.test(source) ||
      !Array.isArray(years) ||
      ![0, 2, 3].includes(years.length) ||
      !years.every((year) => Number.isInteger(year) && year >= 1 && year <= 9998) ||
      !years.every((year, index) => index === 0 || year === years[index - 1] + 1)
    )
      return null
    return {
      kind: 'year_range',
      sourceText: source,
      season: null,
      yearOptions: years,
      status: 'pending',
    }
  }
  const ageQuestion = value as Partial<AgeYearQuestion>
  if (ageQuestion.kind === 'age_year') {
    if (
      precision !== 'range' ||
      typeof ageQuestion.sourceText !== 'string' ||
      !ageClue(ageQuestion.sourceText) ||
      !narrative.toLowerCase().includes(ageQuestion.sourceText.toLowerCase())
    )
      return null
    const years = ageQuestion.yearOptions
    if (
      !Array.isArray(years) ||
      years.length !== 2 ||
      !years.every((year) => Number.isInteger(year) && year >= 1 && year <= 9998) ||
      years[1] !== years[0] + 1
    )
      return null
    return {
      kind: 'age_year',
      sourceText: ageQuestion.sourceText,
      season: null,
      yearOptions: years,
      status: 'pending',
    }
  }
  if (precision !== 'unknown') return null
  const suggestion = value as Partial<DateClarificationSuggestion>
  const source = typeof suggestion.sourceText === 'string' ? suggestion.sourceText.trim() : ''
  if (!source || source.length > 120 || !narrative.toLowerCase().includes(source.toLowerCase()))
    return null
  if (
    !/\b(when|age|aged|little|child\w*|school|college|spring|summer|fall|autumn|winter|before|after|last|ago|during|year|month|week|day)\b/i.test(
      source,
    )
  )
    return null
  if (!['year', 'season_year', 'date'].includes(suggestion.kind ?? '')) return null
  let season: Season | null = null
  if (suggestion.kind === 'season_year') {
    if (!['spring', 'summer', 'fall', 'winter'].includes(suggestion.season ?? '')) return null
    season = suggestion.season!
    if (!new RegExp(`\\b${season === 'fall' ? '(fall|autumn)' : season}\\b`, 'i').test(source))
      return null
  }
  return { kind: suggestion.kind!, sourceText: source, season, status: 'pending' }
}

export function decadeClarification(narrative: string): DateClarification | null {
  const matches = [...narrative.matchAll(/\b(?:early|mid|late)\s+(?:19|20)\d0s\b/gi)]
  if (matches.length !== 1 || /\b\d{4}\b/.test(narrative)) return null
  return { kind: 'year', sourceText: matches[0][0], season: null, status: 'pending' }
}

export function monthAlternativesClarification(narrative: string): DateClarification | null {
  const semantic = resolveMonthAlternatives(narrative)
  if (!semantic) return null
  const monthOptions = [semantic.date!, semantic.endDate!].map(date => ({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) }))
  return { kind: 'month_choices', sourceText: narrative.trim().slice(0, 120), season: null, monthOptions, status: 'pending' }
}

export function schoolYearClarification(narrative: string): DateClarification | null {
  // Grade is historical context, not a calendar year or evidence for today.
  // Do not infer enrollment/cutoff dates from the author's birthday.
  if (/\b\d{4}\b/.test(narrative)) return null
  const match = /\b(?:in|during)\s+(?:my\s+)?(?:[1-9]|1[0-2])(?:st|nd|rd|th)?\s+grade\b|\b(?:in|during)\s+(?:my\s+)?(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)\s+grade\b/i.exec(narrative)
  return match ? { kind: 'year', sourceText: match[0], season: null, status: 'pending' } : null
}

export function dateClarificationQuestion(
  value: DateClarificationSuggestion | AgeYearQuestion | YearRangeQuestion | MonthDayQuestion | MonthChoicesQuestion,
  event?: EventDate,
): string {
  if (value.kind === 'month_choices') {
    const choices = value.monthOptions.map(option => {
      const date = resolveEventDate({ precision: 'month', ...option }).date!
      return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date)
    })
    return `Which month was that: ${choices.join(' or ')}?`
  }
  if (value.kind === 'month_day') return `You mentioned “${value.sourceText}.” Which year was that?`
  if (value.kind === 'year_range') {
    return `You mentioned “${value.sourceText}.” ${clarificationYearOptions({ ...value, status: 'pending' }, event).length > 0 ? 'Do you remember which year?' : 'About what year was that?'}`
  }
  if (value.kind === 'age_year') {
    return `Based on your birthday, you were ${ageClue(value.sourceText)!.age} during parts of ${value.yearOptions[0]} and ${value.yearOptions[1]}. Do you remember which year?`
  }
  const question =
    value.kind === 'season_year'
      ? `Which year was that ${value.season}?`
      : value.kind === 'year'
        ? 'About what year was that?'
        : 'Do you remember the date, or roughly the year?'
  return `You mentioned “${value.sourceText}.” ${question}`
}

export function missingYearClarification(narrative: string, referenceDate?: string): DateClarification | null {
  // Named month/day only: numeric dates are locale-dependent. Do not guess a year
  // or attach one question to narratives with multiple or competing date clues.
  if (/\b\d{4}\b|\b(today|yesterday|tomorrow|last|next|ago)\b/i.test(narrative)) return null
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
  const monthPattern = months.map(name => name === 'may' ? name : `${name}|${name.slice(0, 3)}\\.?`).join('|')
  const matches = [...narrative.matchAll(new RegExp(`\\b(${monthPattern})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'gi'))]
  if (matches.length !== 1) return null
  const match = matches[0]
  if (/^\s*(?:[–-]\s*\d|(?:or|and|to)\s+\d)/i.test(narrative.slice(match.index! + match[0].length))) return null
  const month = months.findIndex(name => name.startsWith(match[1].toLowerCase().replace('.', ''))) + 1
  const day = Number(match[2])
  // A leap year validates Feb 29 without claiming it happened in that year.
  if (!parseCalendarDate(`2000-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`)) return null
  const historical = /\b(childhood|child|kid|little|young|school|college|university|born|used to|back then|growing up|grew up|long ago|years ago|when I was|when we were|at age|aged)\b|\b(?:was|were)\s+(?:around\s+|about\s+)?\d/i.test(narrative)
  return { kind: 'month_day', sourceText: match[0], season: null, month, day, status: 'pending',
    ...(!historical && parseCalendarDate(referenceDate) ? { referenceDate } : {}),
  }
}

export function completeClarificationDate(value: DateClarification, year: number | undefined): SemanticDate {
  if (value.kind !== 'month_day' || !Number.isInteger(year) || year! < 1 || year! > 9998) return { precision: 'unknown' }
  const date = `${String(year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`
  return parseCalendarDate(date) ? { precision: 'day', date } : { precision: 'unknown' }
}

export function clarificationYearOptions(value: DateClarification | null | undefined, event?: EventDate): number[] {
  if (value?.kind === 'month_day') {
    const reference = parseCalendarDate(value.referenceDate)
    if (!reference) return []
    const year = reference.getUTCFullYear()
    // Shortcuts are suggestions only. Exclude impossible dates (e.g. Feb 29
    // in a non-leap year) rather than silently rolling them into March.
    return [year, year - 1].filter(option => completeClarificationDate(value, option).precision === 'day')
  }
  if (!value || !('yearOptions' in value)) return []
  if (value.yearOptions.length) return value.yearOptions
  // Previously saved age-range questions had no buttons. Reuse their stored
  // bounds without re-extracting text or consulting a changed birthday.
  if (value.kind !== 'year_range' || event?.datePrecision !== 'range') return []
  const start = parseCalendarDate(calendarDate(event.date))
  const end = parseCalendarDate(calendarDate(event.endDate))
  if (!start || !end || end < start) return []
  return yearsWithinBounds(start.getUTCFullYear(), end.getUTCFullYear())
}

function yearsWithinBounds(first: number, last: number): number[] {
  return last > first && last - first <= 2
    ? Array.from({ length: last - first + 1 }, (_, index) => first + index) : []
}

// Only offer choices backed by explicit alternatives or birthday-derived bounds.
export function resolveContextDate(
  narrative: string,
  birthDate: string | null | undefined,
  referenceDate: string,
): ReturnType<typeof resolveAgeDate> {
  const years = [...narrative.matchAll(/\b(\d{4})\s+or\s+(\d{4})\b/gi)]
  if (years.length === 1 && (narrative.match(/\b\d{4}\b/g) ?? []).length === 2) {
    const [first, last] = [Number(years[0][1]), Number(years[0][2])].sort((a, b) => a - b)
    if (first >= 1 && last <= 9998 && last === first + 1) {
      return {
        temporal: { precision: 'range', date: `${String(first).padStart(4, '0')}-01-01`, endDate: `${String(last).padStart(4, '0')}-12-31` },
        clarification: {
          kind: 'year_range',
          sourceText: years[0][0],
          season: null,
          yearOptions: [first, last],
          status: 'pending',
        },
      }
    }
  }
  const ages = [...narrative.matchAll(/\bwhen I was (\d{1,3}) or (\d{1,3})\b/gi)]
  if (
    ages.length === 1 &&
    (narrative.match(/\bwhen I was\b/gi) ?? []).length === 1 &&
    !/\b\d{4}\b/.test(narrative) &&
    !/^\s*(?:months?|weeks?|days?|or|and|to|[–-]\s*\d)\b/i.test(
      narrative.slice(ages[0].index! + ages[0][0].length),
    )
  ) {
    const [first, last] = [Number(ages[0][1]), Number(ages[0][2])].sort((a, b) => a - b)
    if (last === first + 1) {
      const lower = resolveAgeDate(`When I was ${first}`, birthDate, referenceDate)
      const upper = resolveAgeDate(`When I was ${last}`, birthDate, referenceDate)
      if (lower && upper) {
        const start = resolveEventDate(lower.temporal).date!
        const end = resolveEventDate(upper.temporal).endDate!
        const firstYear = start.getUTCFullYear()
        const lastYear = end.getUTCFullYear()
        return {
          temporal: { precision: 'range', date: calendarDate(start), endDate: calendarDate(end) },
          clarification: {
            kind: 'year_range',
            sourceText: ages[0][0],
            season: null,
            yearOptions: yearsWithinBounds(firstYear, lastYear),
            status: 'pending',
          },
        }
      }
    }
  }
  return resolveAgeDate(narrative, birthDate, referenceDate)
}

function ageClue(narrative: string): { age: number; sourceText: string } | null {
  // Only the speaker's explicit past age; no inference about relatives or vague childhood.
  const words = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
    'twenty',
  ]
  const matches = [
    ...narrative.matchAll(
      new RegExp(
        `\\bwhen I was (\\d{1,3}|${words.join('|')})(?![\\w-]|\\s+(?:or|and|to|years? old or)\\b)`,
        'gi',
      ),
    ),
  ]
  if (matches.length !== 1) return null
  const match = matches[0]
  // Reject incomplete compounds and ranges ("twenty three", "9–10", "9 or 10").
  if (
    /^\s*(?:[–-]\s*\d|½|(?:months?|weeks?|days?|one|two|three|four|five|six|seven|eight|nine)\b)/i.test(
      narrative.slice(match.index! + match[0].length),
    )
  )
    return null
  const age = /^\d+$/.test(match[1]) ? Number(match[1]) : words.indexOf(match[1].toLowerCase())
  return age >= 0 && age <= 120 ? { age, sourceText: match[0] } : null
}

export function resolveAgeDate(
  narrative: string,
  birthDate: string | null | undefined,
  referenceDate: string,
): {
  temporal: SemanticDate
  clarification: DateClarification | null
} | null {
  const clue = ageClue(narrative)
  const isoBirthDate =
    birthDate && /^\d{2}\/\d{2}\/\d{4}$/.test(birthDate)
      ? `${birthDate.slice(6)}-${birthDate.slice(0, 2)}-${birthDate.slice(3, 5)}`
      : birthDate
  const birth = parseCalendarDate(isoBirthDate)
  const reference = parseCalendarDate(referenceDate)
  if (
    !clue ||
    !birth ||
    !reference ||
    birth > reference ||
    birth.getUTCFullYear() + clue.age + 1 > 9998
  )
    return null
  const anniversary = (age: number, upper = false) => {
    const year = birth.getUTCFullYear() + age
    const monthDay = calendarDate(birth).slice(5)
    const normal = parseCalendarDate(`${String(year).padStart(4, '0')}-${monthDay}`)
    // A Feb 29 birthday may be observed Feb 28 or Mar 1 in a non-leap year.
    return (
      normal ?? parseCalendarDate(`${String(year).padStart(4, '0')}-${upper ? '03-01' : '02-28'}`)!
    )
  }
  const start = anniversary(clue.age)
  const end = anniversary(clue.age + 1, true)
  end.setUTCDate(end.getUTCDate() - 1)
  if (start > reference) return null
  const firstYear = start.getUTCFullYear()
  const lastYear = end.getUTCFullYear()
  const temporal: SemanticDate =
    firstYear === lastYear
      ? { precision: 'year', year: firstYear }
      : { precision: 'range', date: calendarDate(start), endDate: calendarDate(end) }
  if (resolveEventDate(temporal).datePrecision === 'unknown') return null
  return {
    temporal,
    clarification:
      firstYear === lastYear
        ? null
        : {
            kind: 'age_year',
            sourceText: clue.sourceText,
            season: null,
            yearOptions: [firstYear, lastYear],
            status: 'pending',
          },
  }
}
